import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, X, File, Image } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';

interface DocumentDropzoneProps {
  documents: string[];
  onDocumentsChange: (documents: string[]) => void;
}

interface UploadedFile {
  name: string;
  url: string;
  type: string;
  size: number;
}

export const DocumentDropzone = ({ documents, onDocumentsChange }: DocumentDropzoneProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleFiles = async (files: FileList) => {
    if (files.length === 0) return;

    setIsUploading(true);
    const newFiles: UploadedFile[] = [];
    const newDocuments: string[] = [...documents];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          // Upload to order-documents bucket
          const fileName = `doc-${Date.now()}-${file.name}`;
          const { data, error } = await supabase.storage
            .from('order-documents')
            .upload(fileName, file);

          if (error) {
            throw new Error(`Upload failed: ${error.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('order-documents')
            .getPublicUrl(fileName);

          newFiles.push({
            name: file.name,
            url: urlData.publicUrl,
            type: file.type,
            size: file.size
          });

          newDocuments.push(urlData.publicUrl);
        } catch (uploadError: any) {
          console.error('Individual file upload error:', uploadError);
          toast({
            title: "Upload Failed",
            description: `Failed to upload ${file.name}: ${uploadError.message}`,
            variant: "destructive"
          });
        }
      }

      setUploadedFiles(prev => [...prev, ...newFiles]);
      onDocumentsChange(newDocuments);
      
      toast({
        title: "Documents Uploaded",
        description: `${newFiles.length} document(s) uploaded successfully`,
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Error",
        description: error.message || "Failed to upload documents",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newDocuments = documents.filter((_, i) => i !== index);
    
    setUploadedFiles(newFiles);
    onDocumentsChange(newDocuments);
    
    toast({
      title: "Document Removed",
      description: "Document has been removed from the order",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return <Image className="w-8 h-8" />;
    }
    return <FileText className="w-8 h-8" />;
  };

  const isImage = (type: string) => type.startsWith('image/');

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Upload className="w-12 h-12 mx-auto text-primary" />
        <h2 className="text-2xl font-semibold">Other Documents</h2>
        <p className="text-muted-foreground">
          Upload delivery notes, receipts, or other related documents
        </p>
        {uploadedFiles.length > 0 && (
          <Badge variant="secondary" className="text-sm">
            {uploadedFiles.length} document{uploadedFiles.length !== 1 ? 's' : ''} uploaded
          </Badge>
        )}
      </div>

      {/* Upload Area */}
      <Card 
        className={`transition-colors cursor-pointer ${
          dragActive ? 'border-primary bg-primary/5' : 'border-dashed border-muted-foreground/25'
        } ${isUploading ? 'opacity-50 pointer-events-none' : 'hover:border-primary hover:bg-muted/25'}`}
        onDrop={handleDrop}
        onDragOver={handleDrag}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onClick={() => fileInputRef.current?.click()}
      >
        <CardContent className="p-8 text-center">
          <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} />
          <div className="space-y-2">
            <p className="text-lg font-medium">
              {dragActive ? 'Drop files here' : 'Click to upload or drag & drop'}
            </p>
            <p className="text-sm text-muted-foreground">
              PDF, JPG, PNG, DOC files accepted • Max 10MB per file
            </p>
            {isUploading && (
              <div className="flex items-center justify-center space-x-2 mt-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span>Uploading documents...</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
        onChange={(e) => e.target.files && handleFiles(e.target.files)}
        className="hidden"
        disabled={isUploading}
      />

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium">Uploaded Documents</h3>
          <div className="space-y-3">
            {uploadedFiles.map((file, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-center p-4">
                    {isImage(file.type) ? (
                      <div className="w-16 h-16 rounded-lg overflow-hidden mr-4 flex-shrink-0">
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center mr-4 flex-shrink-0">
                        {getFileIcon(file.type)}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="ml-2 flex-shrink-0"
                      disabled={isUploading}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Skip Option */}
      {uploadedFiles.length === 0 && (
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            This step is optional. You can continue without uploading additional documents.
          </p>
        </div>
      )}

      {/* Tips */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">📄 Document Tips</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Include delivery notes, receipts, or quality certificates</li>
            <li>• Drag multiple files at once for faster upload</li>
            <li>• Images will show previews, documents show file icons</li>
            <li>• This step is optional - skip if no additional docs needed</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};